/*
 * InAppVoiceCapture
 *
 * A restful, sanctuary-styled "tap and speak" capture button that lives on the
 * Dashboard. Uses the browser's Web Speech API (webkitSpeechRecognition on
 * Safari/Chrome, SpeechRecognition elsewhere) to transcribe locally, then
 * shows a gentle preview dialog so the user can review before sending.
 *
 * On confirm, the transcript is POSTed to `/api/ambient/capture` using the
 * signed-in user's JWT (no capture token setup required) and appears in the
 * Unprocessed Downloads tray for later promotion.
 *
 * Design notes (per design_guidelines.md):
 *   • Rose-gold ember (#B76E79 / #D7A1A8) accent, charcoal background
 *   • Glassmorphism preview dialog, generous whitespace
 *   • Pulsing ring while listening — respects prefers-reduced-motion
 *   • lucide-react icons only (no emoji as UI icons)
 *   • data-testid on every interactive/informational element
 *   • Accessible aria-labels reflecting state
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, Loader2, Send, Trash2, ShieldCheck, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

// Detect the Web Speech API implementation (Safari uses webkit prefix)
const getSpeechRecognition = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

// Extract a first name from an auth user record. Kept local to keep this
// component self-contained.
const firstNameOf = (user) => {
  if (!user) return '';
  if (user.name && typeof user.name === 'string') {
    const trimmed = user.name.trim();
    if (trimmed) return trimmed.split(/\s+/)[0];
  }
  if (user.email && typeof user.email === 'string') {
    return user.email.split('@')[0].split('.')[0];
  }
  return '';
};

// Capitalize a first name gently ("selah" -> "Selah"). Kept intentionally
// forgiving so an all-caps input like "SELAH" still returns "Selah".
const gentleCapitalize = (name) => {
  if (!name || typeof name !== 'string') return '';
  const trimmed = name.trim();
  if (!trimmed) return '';
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

const STATES = {
  IDLE: 'idle',
  LISTENING: 'listening',
  REVIEWING: 'reviewing',
  SENDING: 'sending',
};

// Whisper streak: soft, reflective language. Never celebratory or gamified —
// just a factual reflection of the last 7 days.
const buildWhisperStreakLine = (count, firstName) => {
  if (count <= 0) return null;
  const noun = count === 1 ? 'whisper' : 'whispers';
  const person = firstName ? `${firstName}, ` : '';
  if (count === 1) {
    return `${person}1 whisper has arrived here in the last seven days.`;
  }
  return `${person}${count} ${noun} have arrived here in the last seven days.`;
};

const InAppVoiceCapture = ({ onCaptured }) => {
  const [state, setState] = useState(STATES.IDLE);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState(null);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [unsupportedDialogOpen, setUnsupportedDialogOpen] = useState(false);
  const [whisperStreak, setWhisperStreak] = useState(0);
  const recognitionRef = useRef(null);
  const isMountedRef = useRef(true);

  const { user } = useAuth() || {};
  const firstName = gentleCapitalize(firstNameOf(user));

  const SpeechRecognitionCtor = getSpeechRecognition();
  const isSupported = Boolean(SpeechRecognitionCtor);

  // ── Cleanup on unmount ────────────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      try {
        recognitionRef.current?.stop();
      } catch (_) {
        /* noop */
      }
    };
  }, []);

  // ── Whisper streak: reflect (not gamify) hands-free activity ──
  // Fetches all ambient captures once and counts those from the last 7 days.
  // This is intentionally lightweight and non-blocking; failures are silent.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.listAmbientCaptures('all');
        if (cancelled || !Array.isArray(list)) return;
        const now = Date.now();
        const cutoff = now - 7 * 24 * 60 * 60 * 1000;
        const count = list.reduce((acc, c) => {
          const t = c?.created_at ? new Date(c.created_at).getTime() : 0;
          return t >= cutoff ? acc + 1 : acc;
        }, 0);
        setWhisperStreak(count);
      } catch (_) {
        // Silent: streak is a "nice to have", never surface an error.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Start / stop dictation ───────────────────────────────────
  const startListening = useCallback(() => {
    if (!isSupported) {
      setUnsupportedDialogOpen(true);
      return;
    }

    setError(null);
    setTranscript('');
    setInterimTranscript('');

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || 'en-US';

    recognition.onresult = (event) => {
      let finalText = '';
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const r = event.results[i];
        if (r.isFinal) {
          finalText += r[0].transcript;
        } else {
          interimText += r[0].transcript;
        }
      }
      if (finalText) {
        setTranscript((prev) => (prev ? `${prev.trim()} ${finalText.trim()}` : finalText.trim()));
      }
      setInterimTranscript(interimText);
    };

    recognition.onerror = (event) => {
      const errType = event?.error;
      console.warn('SpeechRecognition error:', errType);
      if (errType === 'not-allowed' || errType === 'service-not-allowed') {
        setPermissionDialogOpen(true);
      } else if (errType === 'no-speech') {
        // Non-fatal — user didn't say anything.
        setError('I didn’t catch that. Would you like to try again?');
      } else if (errType === 'aborted') {
        // Intentional — do nothing.
      } else {
        setError('Something interrupted the recording. Please try again.');
      }
      if (isMountedRef.current) {
        setState(STATES.IDLE);
        setInterimTranscript('');
      }
    };

    recognition.onend = () => {
      if (!isMountedRef.current) return;
      // If the user tapped Stop and we have any transcript, move to review.
      setState((prev) => {
        if (prev === STATES.LISTENING) {
          // If there's content, show the preview. If nothing, return to idle.
          const combined = (transcriptRef.current || '').trim();
          if (combined.length > 0) return STATES.REVIEWING;
          return STATES.IDLE;
        }
        return prev;
      });
      setInterimTranscript('');
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setState(STATES.LISTENING);
    } catch (err) {
      console.error('Failed to start recognition:', err);
      setError('Could not start the microphone. Please try again.');
      setState(STATES.IDLE);
    }
  }, [SpeechRecognitionCtor, isSupported]);

  // Keep a ref to the latest transcript so onend can inspect it correctly
  const transcriptRef = useRef('');
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  const stopListening = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch (_) {
      /* noop */
    }
    // onend will move state to REVIEWING if there's transcript
  }, []);

  const cancelCapture = useCallback(() => {
    try {
      recognitionRef.current?.abort();
    } catch (_) {
      /* noop */
    }
    setTranscript('');
    setInterimTranscript('');
    setError(null);
    setState(STATES.IDLE);
  }, []);

  // ── Submit to backend (JWT-authenticated) ────────────────────
  const submitCapture = useCallback(async () => {
    const clean = (transcript || '').trim();
    if (clean.length < 3) {
      setError('That’s too short to save. Try a full thought or verse.');
      return;
    }
    setState(STATES.SENDING);
    try {
      const deviceLabel =
        typeof navigator !== 'undefined' && /Mobi|Android/i.test(navigator.userAgent)
          ? 'in-app mobile'
          : 'in-app';
      const result = await api.submitInAppCapture(clean, deviceLabel);
      toast.success('Received — waiting in Unprocessed Downloads', {
        description:
          'You can review and save it to your journey when you’re ready.',
      });
      setTranscript('');
      setInterimTranscript('');
      setError(null);
      setState(STATES.IDLE);
      if (typeof onCaptured === 'function') onCaptured(result);
    } catch (err) {
      console.error('submitInAppCapture error', err);
      toast.error('Could not save that capture', {
        description: err?.message || 'Please try again in a moment.',
      });
      setState(STATES.REVIEWING);
    }
  }, [transcript, onCaptured]);

  // ── UI helpers ───────────────────────────────────────────────
  const combinedPreview = `${transcript}${interimTranscript ? ` ${interimTranscript}` : ''}`.trim();

  const listening = state === STATES.LISTENING;
  const sending = state === STATES.SENDING;
  const reviewing = state === STATES.REVIEWING;

  const buttonLabel = listening
    ? 'Stop and review'
    : reviewing
    ? 'Continue reviewing'
    : 'Tap to speak';

  const handlePrimaryClick = () => {
    if (listening) {
      stopListening();
    } else if (reviewing) {
      // no-op — dialog is open
    } else {
      startListening();
    }
  };

  // ── Render ───────────────────────────────────────────────────
  return (
    <>
      <div
        className="relative rounded-2xl overflow-hidden"
        style={{
          background:
            'linear-gradient(180deg, rgba(183,110,121,0.05) 0%, rgba(28,30,33,0.20) 100%)',
          border: '1px solid rgba(183,110,121,0.20)',
          boxShadow: '0 10px 30px -18px rgba(0,0,0,0.55)',
        }}
        data-testid="in-app-voice-capture"
      >
        {/* Subtle ember glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-16 -right-10 w-[280px] h-[280px] rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(183,110,121,0.16) 0%, rgba(0,0,0,0) 65%)',
            filter: 'blur(20px)',
          }}
        />

        <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-6 p-5 sm:p-6">
          {/* Mic button with pulsing ring */}
          <button
            type="button"
            aria-label={
              listening
                ? 'Stop recording and review'
                : 'Start recording a spoken download'
            }
            onClick={handlePrimaryClick}
            disabled={sending}
            data-testid="voice-capture-mic-button"
            className="relative flex items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(183,110,121,0.65)] transition-colors"
            style={{
              width: 72,
              height: 72,
              background: listening
                ? 'linear-gradient(180deg, #D7A1A8 0%, #B76E79 100%)'
                : 'linear-gradient(180deg, #B76E79 0%, #9E5A64 100%)',
              color: '#1C1C1C',
              boxShadow: listening
                ? '0 0 0 6px rgba(183,110,121,0.20), 0 12px 30px -12px rgba(183,110,121,0.55)'
                : '0 10px 24px -10px rgba(0,0,0,0.55)',
              cursor: sending ? 'not-allowed' : 'pointer',
            }}
          >
            {/* Pulsing ring during listening */}
            {listening && (
              <motion.span
                aria-hidden
                className="absolute inset-0 rounded-full"
                style={{
                  border: '2px solid rgba(215,161,168,0.55)',
                }}
                initial={{ opacity: 0.6, scale: 1 }}
                animate={{ opacity: 0, scale: 1.6 }}
                transition={{ repeat: Infinity, duration: 1.8, ease: 'easeOut' }}
              />
            )}
            {sending ? (
              <Loader2 size={28} className="animate-spin" />
            ) : listening ? (
              <MicOff size={28} strokeWidth={2.2} />
            ) : (
              <Mic size={28} strokeWidth={2.2} />
            )}
          </button>

          {/* "Tap to stop" pill — high-visibility hint while listening.
              Positioned just below the mic button (or beside it on mobile
              stacked layout) so users never wonder how to end recording. */}
          {listening && (
            <div
              className="hidden sm:flex absolute"
              style={{
                left: '1.5rem',
                top: 'calc(1.5rem + 78px)',
              }}
              aria-hidden
            >
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.18em]"
                style={{
                  background: 'rgba(183,110,121,0.16)',
                  border: '1px solid rgba(183,110,121,0.35)',
                  color: '#E1B4BA',
                }}
                data-testid="voice-capture-tap-to-stop-hint"
              >
                Tap to stop
              </span>
            </div>
          )}

          {/* Copy & privacy hint */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3
                className="text-[17px] sm:text-[18px] font-semibold"
                style={{
                  color: 'var(--sd-fg-strong, #F2F2F2)',
                  fontFamily: '"Playfair Display", serif',
                  letterSpacing: '-0.01em',
                }}
                data-testid="voice-capture-title"
              >
                Speak a Download
              </h3>
              {!isSupported && (
                <span
                  className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full"
                  style={{
                    background: 'rgba(224,177,90,0.12)',
                    color: '#E0B15A',
                    border: '1px solid rgba(224,177,90,0.25)',
                  }}
                  data-testid="voice-capture-unsupported-badge"
                >
                  Voice not supported here
                </span>
              )}
            </div>
            <p
              className="text-[13.5px] leading-relaxed"
              style={{ color: 'var(--sd-muted-fg, #CFC7C8)' }}
              data-testid="voice-capture-subtitle"
            >
              {listening
                ? "I'm listening — tap the mic again to stop."
                : reviewing
                ? "Take a moment. Review what you shared before it lands in your tray."
                : firstName
                ? `No setup needed, ${firstName}. Tap the mic, speak your download, and it lands in Unprocessed Downloads for review.`
                : 'No setup needed. Tap the mic, speak your download, and it lands in Unprocessed Downloads for review.'}
            </p>

            {/* Live interim transcript preview */}
            {listening && combinedPreview && (
              <p
                className="mt-3 text-[13px] italic leading-relaxed"
                style={{
                  color: 'var(--sd-fg, #F2F2F2)',
                  fontFamily: '"Crimson Text", serif',
                }}
                data-testid="voice-capture-interim-preview"
              >
                &ldquo;{combinedPreview}&rdquo;
              </p>
            )}

            {/* Error state */}
            {error && !listening && !reviewing && (
              <p
                className="mt-3 flex items-start gap-2 text-[12.5px]"
                style={{ color: '#E5A0A0' }}
                data-testid="voice-capture-error"
              >
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </p>
            )}

            {/* Privacy footnote */}
            <div className="mt-3 flex items-center gap-2">
              <ShieldCheck size={12} style={{ color: 'rgba(207,199,200,0.70)' }} />
              <span
                className="text-[11px]"
                style={{ color: 'rgba(207,199,200,0.70)' }}
                data-testid="voice-capture-privacy-note"
              >
                Held in your Unprocessed inbox until you review and save.
              </span>
            </div>

            {/* Whisper reflection (non-gamified) — only visible when there
                have been captures in the last 7 days. Deliberately quiet:
                reflection, not achievement. */}
            {whisperStreak > 0 && !listening && !reviewing && (
              <p
                className="mt-2 text-[11px] italic"
                style={{
                  color: 'rgba(207,199,200,0.60)',
                  fontFamily: '"Crimson Text", serif',
                }}
                data-testid="voice-capture-whisper-streak"
              >
                {buildWhisperStreakLine(whisperStreak, firstName)}
              </p>
            )}
          </div>

          {/* Secondary label under button on mobile — visually hidden helper text */}
          <span className="sr-only" aria-live="polite">
            {buttonLabel}
          </span>
        </div>
      </div>

      {/* ── Review dialog (post-listening) ── */}
      <Dialog
        open={reviewing}
        onOpenChange={(open) => {
          if (!open && !sending) cancelCapture();
        }}
      >
        <DialogContent
          className="max-w-lg rounded-2xl"
          style={{
            background: 'rgba(28,30,33,0.98)',
            border: '1px solid rgba(245,245,245,0.14)',
            boxShadow: '0 20px 55px -20px rgba(0,0,0,0.65)',
            backdropFilter: 'blur(18px)',
          }}
          data-testid="voice-capture-review-dialog"
        >
          <DialogHeader>
            <DialogTitle
              className="text-[20px]"
              style={{
                color: '#F2F2F2',
                fontFamily: '"Playfair Display", serif',
                letterSpacing: '-0.01em',
              }}
            >
              Review your download
            </DialogTitle>
            <DialogDescription
              className="text-[13px]"
              style={{ color: 'rgba(207,199,200,0.85)' }}
            >
              Edit if you’d like. When you’re ready, we’ll place
              it in Unprocessed Downloads for reflection.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2">
            <Textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              placeholder="Your spoken download will appear here."
              className="min-h-[140px] text-[14px] leading-relaxed"
              style={{
                background: 'rgba(245,245,245,0.06)',
                border: '1px solid rgba(245,245,245,0.14)',
                color: '#F2F2F2',
                borderRadius: 12,
                fontFamily: '"Crimson Text", serif',
              }}
              data-testid="voice-capture-review-textarea"
            />
            <p
              className="mt-2 text-[11.5px]"
              style={{ color: 'rgba(207,199,200,0.65)' }}
            >
              {transcript.trim().length} character{transcript.trim().length === 1 ? '' : 's'}
              {' · '}
              Held in plaintext in your inbox until you Save to Journey.
            </p>
          </div>

          <DialogFooter className="mt-4 flex flex-row items-center justify-between gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={cancelCapture}
              disabled={sending}
              data-testid="voice-capture-discard-button"
              className="text-[13px]"
              style={{
                color: 'rgba(207,199,200,0.85)',
              }}
            >
              <Trash2 size={14} className="mr-2" />
              Discard
            </Button>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  // "Speak more" — resume listening, keeping current transcript
                  setState(STATES.IDLE);
                  setTimeout(() => startListening(), 40);
                }}
                disabled={sending}
                data-testid="voice-capture-speak-more-button"
                className="text-[13px]"
                style={{
                  color: '#F2F2F2',
                  border: '1px solid rgba(245,245,245,0.14)',
                  background: 'rgba(245,245,245,0.06)',
                }}
              >
                <Mic size={14} className="mr-2" />
                Speak more
              </Button>
              <Button
                type="button"
                onClick={submitCapture}
                disabled={sending || transcript.trim().length < 3}
                data-testid="voice-capture-send-button"
                className="text-[13px] font-semibold"
                style={{
                  background:
                    'linear-gradient(180deg, #D7A1A8 0%, #B76E79 100%)',
                  color: '#1C1C1C',
                  border: '1px solid #B76E79',
                  boxShadow: '0 8px 22px -10px rgba(183,110,121,0.55)',
                }}
              >
                {sending ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Sending
                  </>
                ) : (
                  <>
                    <Send size={14} className="mr-2" />
                    Send to Unprocessed
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Mic permission denied dialog ── */}
      <AlertDialog open={permissionDialogOpen} onOpenChange={setPermissionDialogOpen}>
        <AlertDialogContent
          className="max-w-lg rounded-2xl"
          style={{
            background: 'rgba(28,30,33,0.98)',
            border: '1px solid rgba(245,245,245,0.14)',
          }}
          data-testid="voice-capture-permission-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle
              className="flex items-center gap-2 text-[18px]"
              style={{
                color: '#F2F2F2',
                fontFamily: '"Playfair Display", serif',
              }}
            >
              <MicOff size={18} style={{ color: '#B76E79' }} />
              Microphone access is off
            </AlertDialogTitle>
            <AlertDialogDescription
              className="text-[13px] leading-relaxed"
              style={{ color: 'rgba(207,199,200,0.85)' }}
            >
              Selah Downloads listens on your device only. To use in-app voice capture,
              enable microphone access:
              <br />
              <br />
              <strong style={{ color: '#F2F2F2' }}>iPhone (Safari / PWA):</strong>
              {' '}Settings → Safari → Microphone → Allow.
              <br />
              <strong style={{ color: '#F2F2F2' }}>Android (Chrome):</strong>
              {' '}Site settings → Microphone → Allow.
              <br />
              <strong style={{ color: '#F2F2F2' }}>Desktop:</strong>
              {' '}Click the lock icon in the URL bar → allow microphone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setPermissionDialogOpen(false)}
              data-testid="voice-capture-permission-close"
              style={{
                background: '#B76E79',
                color: '#1C1C1C',
              }}
            >
              <X size={14} className="mr-2" />
              Got it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Browser unsupported dialog ── */}
      <AlertDialog open={unsupportedDialogOpen} onOpenChange={setUnsupportedDialogOpen}>
        <AlertDialogContent
          className="max-w-lg rounded-2xl"
          style={{
            background: 'rgba(28,30,33,0.98)',
            border: '1px solid rgba(245,245,245,0.14)',
          }}
          data-testid="voice-capture-unsupported-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle
              className="flex items-center gap-2 text-[18px]"
              style={{
                color: '#F2F2F2',
                fontFamily: '"Playfair Display", serif',
              }}
            >
              <AlertCircle size={18} style={{ color: '#E0B15A' }} />
              This browser can’t listen yet
            </AlertDialogTitle>
            <AlertDialogDescription
              className="text-[13px] leading-relaxed"
              style={{ color: 'rgba(207,199,200,0.85)' }}
            >
              Your browser doesn’t support in-app voice capture. You have
              two calm alternatives:
              <br />
              <br />
              <strong style={{ color: '#F2F2F2' }}>1.</strong>{' '}
              Open Selah in <em>Safari (iOS)</em> or <em>Chrome (Android/Desktop)</em>
              {' '}— both support voice.
              <br />
              <strong style={{ color: '#F2F2F2' }}>2.</strong>{' '}
              Use the <em>Begin a Download</em> button at the top of your dashboard
              to type your download instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setUnsupportedDialogOpen(false)}
              data-testid="voice-capture-unsupported-close"
              style={{
                background: '#B76E79',
                color: '#1C1C1C',
              }}
            >
              Understood
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default InAppVoiceCapture;
